import type { ChatClient } from '@wtfalch/chat';
import { Button, Icon, IdentityDisc } from '@wtfalch/design';
import { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

import { showIncomingCallNotification } from '../notify.ts';
import { pathForChat } from '../routes.ts';
import { type CallSessionValue, useCallSession } from './CallSession.tsx';
import { createRinger, type IncomingCall, type Ringer, watchIncomingCalls } from './ringing.ts';
import type { RoomCallBinding } from './roomCalls.ts';

/** The words a person can decline with, one tap each. They go to the calls
 *  service as the decline reason and the caller sees them. */
export const DECLINE_REASONS = [
  'In a meeting, ten minutes',
  'Send it as a message instead',
] as const;

export type AnswerChoice = { video: boolean };

/** The incoming call, drawn in its own React root on `document.body` so it
 *  shows over whichever section is open. Answer joins with the camera on,
 *  Audio only joins without it, Decline (or a reason) sends the caller word. */
export function IncomingCallBanner({
  calls,
  onAnswer,
  onDecline,
}: {
  calls: IncomingCall[];
  onAnswer: (call: IncomingCall, choice: AnswerChoice) => void;
  onDecline: (call: IncomingCall, reason?: string) => void;
}) {
  // Focus is never moved here: a call that arrives while someone types must
  // not turn their next Space or Enter into an answer. The banner is an
  // alert, so a screen reader announces it; Escape declines once focus is in
  // it.
  if (calls.length === 0) return null;
  return (
    <div
      style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1000, display: 'grid', gap: 8 }}
    >
      {calls.map((call) => (
        <div
          key={call.roomId}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onDecline(call);
          }}
          role="alert"
          aria-label={`Incoming call in ${call.roomName}`}
          className="flex flex-col gap-3 p-4 rounded-md"
          style={{
            background: 'var(--panel)',
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-3)',
            color: 'var(--text)',
            maxWidth: 360,
          }}
        >
          <span className="flex items-center gap-3">
            <IdentityDisc name={call.callerName} address={call.callerName} />
            <span className="flex flex-col min-w-0">
              <strong className="truncate">{call.callerName}</strong>
              <span className="text-xs text-muted">Call · from #{call.roomName}</span>
            </span>
          </span>
          <span className="flex flex-wrap gap-2">
            <Button kind="primary" onPress={() => onAnswer(call, { video: true })}>
              <Icon name="phone" size={16} />
              <span>Answer</span>
            </Button>
            <Button onPress={() => onAnswer(call, { video: false })}>Audio only</Button>
            <Button kind="danger" onPress={() => onDecline(call)}>
              <Icon name="phone-down" size={16} />
              <span>Decline</span>
            </Button>
          </span>
          <span className="flex flex-col gap-2">
            <span className="text-xs text-muted">Or answer with a reason:</span>
            <span className="flex flex-wrap gap-2">
              {DECLINE_REASONS.map((reason) => (
                <Button key={reason} size="sm" onPress={() => onDecline(call, reason)}>
                  {reason}
                </Button>
              ))}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

/** Watches the chat client for call notices and rings for a live one. Does
 *  nothing without a chat client and a calls binding. */
export function useIncomingCalls(
  chat: ChatClient | null,
  calls: RoomCallBinding | undefined,
  options: { agoraId: string; go: (path: string) => void; ringer?: Ringer },
): void {
  const { agoraId, go, ringer } = options;
  // Read through a ref: the call session changes identity as a call starts
  // and ends, and the watcher must outlive that without ringing again.
  const session = useCallSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  useEffect(() => {
    if (!chat || !calls) return;
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);
    let watcher: ReturnType<typeof watchIncomingCalls> | null = null;
    watcher = watchIncomingCalls(chat, calls.client, {
      ringer: ringer ?? createRinger(),
      onChange: (incoming) =>
        root.render(
          <IncomingCallBanner
            calls={incoming}
            onAnswer={(call, choice) => {
              watcher?.dismiss(call.roomId);
              void answerIncomingCall({
                call,
                video: choice.video,
                calls,
                session: sessionRef.current,
                go,
                agoraId,
              });
            }}
            onDecline={(call, reason) => {
              watcher?.dismiss(call.roomId);
              declineCall(calls, call, reason);
            }}
          />,
        ),
      onRing: (call) => showIncomingCallNotification(call, { agoraId, go }),
    });
    return () => {
      watcher?.stop();
      root.unmount();
      container.remove();
    };
  }, [chat, calls, agoraId, go, ringer]);
}

/**
 * Answering joins: the person lands in the room already on the call, full
 * screen. A refusal (already on another call) is logged here and reaches the
 * person through the call session's own notice.
 */
export async function answerIncomingCall({
  call,
  video,
  calls,
  session,
  go,
  agoraId,
}: {
  call: IncomingCall;
  video: boolean;
  calls: RoomCallBinding;
  session: CallSessionValue | null;
  go: (path: string) => void;
  agoraId: string;
}): Promise<void> {
  const path = pathForChat(agoraId, { roomId: call.roomId, threadId: null, eventId: null });
  go(path);
  if (!session) return;
  const result = await session.start(
    { roomId: call.roomId, title: call.roomName, path, video },
    calls,
  );
  if (result.ok) session.setFocused(true);
  else {
    console.error('Could not answer the call:', result.error);
    session.notify(result.error);
  }
}

/** Tells the caller's side the call was declined, and why. Best effort: the
 *  ring has already stopped here, and a refused request changes nothing the
 *  person can act on. */
export function declineCall(calls: RoomCallBinding, call: IncomingCall, reason = 'Declined'): void {
  try {
    calls.client
      .decline(call.sessionId, { reason, requestId: crypto.randomUUID() })
      .catch((error: unknown) => console.error('Could not send the decline:', error));
  } catch (error) {
    console.error('Could not send the decline:', error);
  }
}
